import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Proximiio, { ImageDetection } from "proximiio-js-library";
import { v4 as uuidv4 } from "uuid";
import {
  osName,
  browserName,
  mobileModel,
  mobileVendor,
  deviceType,
} from "react-device-detect";
import { t } from "i18next";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import {
  FaLocationDot,
  FaFlag,
  FaCirclePlus,
  FaCircleCheck,
  FaCheck,
  FaPlus,
} from "react-icons/fa6";
import { IoCloseSharp } from "react-icons/io5";
import Feature from "proximiio-js-library/lib/models/feature";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { cn, getFilteredItems, getSortedPOIs } from "@/lib/utils";
import { SortedPoiItemModel } from "@/models/sortedPoiItem.model";

import useMapStore from "@/store/mapStore";
import useFeaturesStore from "@/store/featuresStore";
import { excludedAmenities } from "@/store/data";
import { StopModel } from "@/models/stop.model";
import StopsContext from "./ui/stops";
import { TbCurrentLocation, TbHome } from "react-icons/tb";

function RouteForm() {
  const features = useFeaturesStore((state) => state.features);
  const showCustomRoutePicker = useMapStore(
    (state) => state.showCustomRoutePicker,
  );
  const currentLang = useMapStore((state) => state.currentLang);
  const floors = useMapStore((state) => state.floors);
  const startFeature = useMapStore((state) => state.startFeature);
  const finishFeature = useMapStore((state) => state.finishFeature);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const activeKiosk = useMapStore((state) => state.activeKiosk);
  const appSession = useMapStore((state) => state.appSession);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const filterCategories = useMapStore((state) => state.filterCategories);
  const stopsStore = useMapStore((state) => state.stops);
  const map = useMapStore((state) => state.map);
  const haveCustomPosition = useMapStore((state) => state.haveCustomPosition);
  const verticalKioskMode = useMapStore((state) => state.verticalKioskMode);

  const [startSearch, setStartSearch] = useState("");
  const [finishSearch, setFinishSearch] = useState("");
  const [activeMenu, setActiveMenu] = useState<"start" | "finish" | undefined>(
    "start",
  );
  const [foundInTitle, setFoundInTitle] = useState<SortedPoiItemModel[]>([]);
  const [foundInDescription, setFoundInDescription] = useState<
    SortedPoiItemModel[]
  >([]);
  const [enableGVision, setEnableGVision] = useState(
    import.meta.env.VITE_WAYDINDING_ENABLE_GVISION === "true",
  );
  const multipointEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_MULTIPOINT === "true" && !kioskMode;
  const combiNavEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_COMBINAV === "true" && !kioskMode;

  const setStartFeature = useMapStore((state) => state.setStartFeature);
  const setFinishFeature = useMapStore((state) => state.setFinishFeature);
  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const cancelRouting = useMapStore((state) => state.cancelRouting);
  const centerToFinish = useMapStore((state) => state.centerToFinish);
  const setRouteStart = useMapStore((state) => state.setRouteStart);
  const setStopsStore = useMapStore((state) => state.setStops);
  const setShowCustomRoutePicker = useMapStore(
    (state) => state.setShowCustomRoutePicker,
  );
  const resetView = useMapStore((state) => state.resetView);
  const setCameraRecognitionFeature = useMapStore(
    (state) => state.setCameraRecognitionFeature,
  );
  const setNavMode = useMapStore((state) => state.setNavMode);

  const [stops, setStops] = useState<StopModel[]>(
    multipointEnabled
      ? stopsStore.length > 0
        ? stopsStore
        : [
            {
              id: uuidv4(),
            },
            {
              id: uuidv4(),
              feature: finishFeature,
              searchValue: finishFeature.properties?.title,
            },
          ]
      : [],
  );
  const [focusedStop, setFocusedStop] = useState<number | null>(0);

  useEffect(() => {
    if (startFeature?.id) {
      setActiveMenu("finish");
    }
    document.body.style.setProperty("overflow", "auto", "important");
    return () => {
      document.body.style.removeProperty("overflow");
    };
  }, []);

  useEffect(() => {
    const search = activeMenu === "start" ? startSearch : finishSearch;
    const pois = getSortedPOIs({ features, currentLang, floors });

    const filtered = getFilteredItems({
      items: pois,
      search,
      language: currentLang,
    }) as SortedPoiItemModel[];
    const foundInTitle = filtered.filter((item) => !item.foundInDescription);
    const foundInDescription = filtered.filter(
      (item) => item.foundInDescription,
    );

    setFoundInTitle(foundInTitle);
    setFoundInDescription(foundInDescription);
  }, [startSearch, finishSearch, activeMenu, currentLang, features, floors]);

  useEffect(() => {
    if (!enableGVision) {
      return;
    }
    if (navigator.mediaDevices) {
      navigator.mediaDevices.enumerateDevices().then(function (devices) {
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput",
        );
        setEnableGVision(videoDevices.length > 0);
      });
    } else {
      setEnableGVision(false);
    }
  }, [setEnableGVision, enableGVision]);

  const onSelectHandler = ({
    poi,
    cameraRecognized = false,
    defaultFeature,
  }: {
    poi: SortedPoiItemModel;
    cameraRecognized?: boolean;
    defaultFeature?: Feature;
  }) => {
    const feature = defaultFeature
      ? defaultFeature
      : features.find((item: Feature) => item.id === poi.id)!;
    if (feature) {
      if (
        multipointEnabled &&
        stops.length > 1 &&
        focusedStop !== null &&
        activeMenu === "finish"
      ) {
        const updatedStops = [...stops];
        updatedStops[focusedStop].poi = poi;
        updatedStops[focusedStop].feature = feature;
        updatedStops[focusedStop].searchValue = poi.properties.title;
        if (updatedStops[focusedStop + 1]) {
          setFocusedStop(focusedStop + 1);
        }
        setStops(updatedStops);

        if (cameraRecognized) {
          const pathPointFeature = map.findPathPoint({
            featureId: updatedStops[0].feature!.id,
            displayOnMap: true,
          });
          map.centerToFeature(pathPointFeature.id);
          setCameraRecognitionFeature(pathPointFeature);
        }
        console.log("set stops", updatedStops);
      } else {
        if (cameraRecognized) {
          const pathPointFeature = map.findPathPoint({
            featureId: feature.id,
            displayOnMap: true,
          });
          map.centerToFeature(pathPointFeature.id);
          setCameraRecognitionFeature(pathPointFeature);
        } else {
          if (activeMenu === "start") {
            setStartFeature(feature);
            if (!mobileMode) {
              setRouteStart(feature);
            }
          }
          if (activeMenu === "finish") {
            setFinishFeature(feature);
            setRouteFinish(feature);
            centerToFinish();
          }
        }
        setActiveMenu(undefined);
        setStops([{ id: uuidv4() }]);
        setStopsStore([]);
      }

      //save feature select log
      console.log("LOG: save feature select log");
      let featureAmenityCategory = "";
      const featureSection = feature?.properties?.amenity
        ? filterCategories.find((category) =>
            category.items.find((item) => {
              if (
                (Array.isArray(item.id) &&
                  item.id.includes(feature.properties.amenity)) ||
                item.id === feature.properties.amenity
              ) {
                featureAmenityCategory = item.title;
                return true;
              }
            }),
          )
        : undefined;

      const userData = {
        osName,
        browserName,
        mobileModel,
        mobileVendor,
        deviceType:
          kioskMode && !mobileMode && activeKiosk?.name
            ? activeKiosk?.name
            : deviceType,
      };

      new Proximiio.InteractionLogger({
        interactionType: "select",
        targetElementType: "feature",
        targetElementId: feature ? feature.id : undefined,
        targetElementTitle: feature ? feature.properties.title : undefined,
        targetElementAmenity: t(featureAmenityCategory, { lng: "en" }),
        targetElementAmenityCategory: featureSection
          ? t(featureSection.title, { lng: "en" })
          : "",
        kioskId: activeKiosk?.id ? activeKiosk?.id : undefined,
        kioskTitle: activeKiosk?.name ? activeKiosk?.name : undefined,
        metadata: userData,
        language: currentLang,
        session: appSession,
      });
    }
    console.log("selected", feature);
  };

  const inputChangeHandler = (input: "start" | "finish", value: string) => {
    if (input === "start" && startFeature) {
      setStartFeature({} as Feature);
      setStartSearch(value);
    }
    if (input === "finish" && finishFeature) {
      setFinishFeature({} as Feature);
      setFinishSearch(value);
    }
  };

  const inputFocusHandler = (input: "start" | "finish") => {
    //if (activeMenu === undefined) {
    setActiveMenu(input);
    //}
    if (input === "start") {
      setStartSearch(
        startFeature.properties?.title ? startFeature?.properties?.title : "",
      );
    }
    if (input === "finish" && finishFeature?.properties?.title) {
      setFinishSearch(finishFeature?.properties?.title);
    }
  };

  const inputBlurHandler = () => {
    setTimeout(() => {
      if (!startFeature?.properties?.title) {
        // resetView();
      }
      // setActiveMenu(undefined);
    }, 500);
  };

  const onCloseHandle = () => {
    console.log("close handler", stops);
    if (!stops[0]?.searchValue) {
      resetView();
      return;
    }
    cancelRouting();
    /*if (input === "start") {
      setStartFeature({} as Feature);
      setStartSearch("");
    }
    if (input === "finish") {
      setFinishFeature({} as Feature);
      setFinishSearch("");
    }
    if (
      !activeMenu &&
      (!startFeature?.properties?.title || !finishFeature.properties?.title)
    ) {
      resetView();
    }*/
  };

  const recognizeLocationHandler = () => {
    if (haveCustomPosition) {
      const feature = map
        .getMapState()
        .dynamicFeatures.features.find(
          (f: Feature) => f.id === "custom-position",
        );
      const pois = getSortedPOIs({
        features: [feature],
        currentLang,
        floors,
      });
      pois[0].properties.title = t("current-location");
      onSelectHandler({
        poi: pois[0],
        cameraRecognized: false,
        defaultFeature: feature,
      });
      return;
    }
    ImageDetection.init(
      {
        gVisionApiKey: import.meta.env.VITE_WAYFINDING_GVISION_APIKEY,
        pois: getSortedPOIs({ features, currentLang, floors }).filter(
          (item) => {
            if (excludedAmenities.includes(item.properties.amenity))
              return false;
            return true;
          },
        ),
        cameraText: t("gvision-camera-text"),
        tryAgainButtonText: t("try-again"),
        confirmButtonText: t("confirm"),
        noResultsText: t("gvision-no-results"),
        resultsHeadingText: t("gvision-results-header"),
        returnResults: 1,
        dir: currentLang === "ar" ? "rtl" : "ltr",
      },
      (item) => {
        onSelectHandler({ poi: item, cameraRecognized: true });
      },
    );
  };

  const addStopHandler = () => {
    if (stops.length === 5) return;
    setStops([...stops, { id: uuidv4() }]);
    setFocusedStop(null);
    setTimeout(() => {
      const undefinedStopIndex = stops.findIndex((stop) => !stop.searchValue);
      setFocusedStop(undefinedStopIndex !== -1 ? undefinedStopIndex : 0);
    });
  };

  const removeStopHandler = (index: number) => {
    const newStops = [...stops];
    newStops.splice(index, 1);
    if (newStops.length === 1) {
      resetView();
      return;
    }
    const undefinedStopIndex = newStops.findIndex((stop) => !stop.searchValue);
    setFocusedStop(undefinedStopIndex !== -1 ? undefinedStopIndex : 0);
    setStops([...newStops]);
    setActiveMenu("start");
  };

  const stopsChangedHandler = (stops: StopModel[]) => {
    setActiveMenu("finish");
    const search = focusedStop !== null ? stops[focusedStop].searchValue : "";
    setFinishSearch(search || "");
    setStops([...stops]);
  };

  const stopFocusChangeHandler = (index: number | null) => {
    if (index !== null) {
      setActiveMenu("finish");
      const search = stops[index].searchValue;
      setFinishSearch(search || "");
      setFocusedStop(index);
    }
  };

  const multipointDoneHandler = () => {
    const stopsWithFeature = stops.filter((stop) => stop.feature);
    if (stopsWithFeature.length === stops.length && stops.length > 1) {
      setStopsStore(stops);
      setRouteFinish(
        stops.length > 2 ? stops[0].feature! : stops[stops.length - 1].feature!,
      );
      //if (stops.length > 2) {
      setStartFeature(stops[0].feature!);
      //}
      setShowCustomRoutePicker(false);
      centerToFinish();
    }
  };

  const navigateFromHomeHandler = () => {
    setNavMode("city-to");
    setShowCustomRoutePicker(true);
  };

  return (
    <>
      {mobileMode && (
        <div className="absolute z-50 h-full w-full bg-background/80 backdrop-blur-sm"></div>
      )}
      <motion.div
        key="list"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          "absolute left-3 right-3 top-3 z-100 flex h-[530px] flex-col rounded-xl p-5 lg:left-10 lg:top-10 lg:h-[600px] lg:max-h-none",
          activeMenu && "bg-white shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]",
          verticalKioskMode &&
            "fixed bottom-12 w-[90%] kiosk:left-[5%] kiosk:top-auto kiosk:h-[28%]",
        )}
      >
        <div
          className="flex h-[500px] flex-col items-center gap-3 lg:h-[570px]"
          style={{
            //@ts-expect-error color
            "--custom-handle-bg": `var(--primary)`,
          }}
        >
          {!multipointEnabled && (
            <div className="relative flex w-full items-center">
              <FaLocationDot className="absolute text-primary group-data-[dir=ltr]:left-4 group-data-[dir=rtl]:right-4" />
              <IoCloseSharp
                className="absolute cursor-pointer text-2xl text-primary hover:text-rose-600 group-data-[dir=ltr]:right-4 group-data-[dir=rtl]:left-4"
                onClick={() => onCloseHandle()}
              />
              <Input
                type="text"
                placeholder={t("choose-start")}
                className="rounded-full border-2 border-primary px-10 text-base"
                disabled={activeMenu === "finish" && stops.length === 1}
                autoFocus={activeMenu === "start"}
                value={
                  startFeature?.properties?.title
                    ? startFeature.properties.title
                    : startSearch
                }
                onChange={(e) => inputChangeHandler("start", e.target.value)}
                onFocus={() => inputFocusHandler("start")}
                onBlur={inputBlurHandler}
              />
            </div>
          )}
          {showCustomRoutePicker && stops.length > 1 && multipointEnabled && (
            <StopsContext
              stops={stops}
              focusedStop={focusedStop}
              onRemove={removeStopHandler}
              onStopsChanged={stopsChangedHandler}
              onFocusChange={stopFocusChangeHandler}
              type={"input"}
            />
          )}
          {showCustomRoutePicker && !multipointEnabled && (
            <div className="relative flex w-full items-center">
              <FaFlag className="absolute text-primary group-data-[dir=ltr]:left-4 group-data-[dir=rtl]:right-4" />
              <IoCloseSharp
                className="absolute cursor-pointer text-2xl text-primary hover:text-rose-600 group-data-[dir=ltr]:right-4 group-data-[dir=rtl]:left-4"
                onClick={() => onCloseHandle()}
              />
              <Input
                type="text"
                placeholder={t("destination")}
                className="rounded-full border-2 border-primary px-10 text-base"
                disabled={activeMenu === "start"}
                autoFocus={activeMenu === "finish"}
                value={
                  finishFeature?.properties?.title
                    ? finishFeature.properties.title
                    : finishSearch
                }
                onChange={(e) => inputChangeHandler("finish", e.target.value)}
                onFocus={() => inputFocusHandler("finish")}
                onBlur={inputBlurHandler}
              />
            </div>
          )}
          {activeMenu !== undefined &&
            (verticalKioskMode ? (
              <div
                className="absolute top-0 flex items-center justify-center gap-2 group-data-[dir=ltr]:right-0 group-data-[dir=rtl]:left-0 group-data-[dir=ltr]:pr-5 group-data-[dir=rtl]:pl-5"
                style={{
                  top: stops.length > 1 ? `${stops.length * 75}px` : "auto",
                  marginTop: stops.length > 1 ? `-54px` : 0,
                }}
              >
                {multipointEnabled && (
                  <Button
                    className="h-[66px] w-[66px] rounded-full border-primary text-lg text-primary"
                    onClick={addStopHandler}
                    disabled={stops.length === 5}
                    size={mobileMode ? "sm" : "default"}
                    variant="outline"
                  >
                    <FaPlus className="text-3xl" />
                  </Button>
                )}
                {multipointEnabled && stops.length > 1 && (
                  <Button
                    className="h-[66px] w-[66px] rounded-full border-primary text-lg"
                    onClick={multipointDoneHandler}
                    disabled={
                      stops.filter((stop) => !stop.feature?.id).length > 0
                    }
                    size={mobileMode ? "sm" : "default"}
                  >
                    <FaCheck className="text-3xl" />
                  </Button>
                )}
              </div>
            ) : (
              <div className="mt-0 flex items-center justify-center gap-6">
                {multipointEnabled && (
                  <Button
                    className="rounded-full px-8"
                    onClick={addStopHandler}
                    disabled={stops.length === 5}
                    size={mobileMode ? "sm" : "default"}
                  >
                    <FaCirclePlus className="group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />{" "}
                    {t("add-stop")}
                  </Button>
                )}
                {multipointEnabled && stops.length > 1 && (
                  <Button
                    className="rounded-full px-8"
                    onClick={multipointDoneHandler}
                    disabled={
                      stops.filter((stop) => !stop.feature?.id).length > 0
                    }
                    size={mobileMode ? "sm" : "default"}
                  >
                    <FaCircleCheck className="text-base group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />{" "}
                    {t("done")}
                  </Button>
                )}
              </div>
            ))}
          {activeMenu !== undefined && (
            <OverlayScrollbarsComponent
              className={cn(
                "w-full overflow-auto group-data-[dir=ltr]:pr-7 group-data-[dir=rtl]:pl-7",
                enableGVision && "max-h-48",
              )}
              options={{
                scrollbars: { autoHide: "move", autoHideDelay: 3000 },
                overflow: { x: "hidden" },
              }}
              defer
            >
              <div>
                {combiNavEnabled &&
                  (stops.length === 1 || !multipointEnabled) && (
                    <ul className="mb-2 flex flex-col gap-2">
                      <li
                        className="flex cursor-pointer items-center lg:hover:text-primary"
                        onClick={navigateFromHomeHandler}
                      >
                        <TbHome className="h-5 w-5 group-data-[dir=ltr]:mr-1 group-data-[dir=rtl]:ml-1" />
                        <span className="flex-1">
                          {t("navigate-from-home")}
                        </span>
                      </li>
                    </ul>
                  )}
                {foundInTitle.length > 0 && (
                  <ul className="flex flex-col gap-2">
                    <li className="font-semibold text-muted-foreground/80">
                      {t("suggestions")}
                    </li>
                    {((mobileMode && enableGVision) ||
                      (haveCustomPosition &&
                        stops.length > 1 &&
                        stops.filter(
                          (stop) => stop.poi?.id === "custom-position",
                        ).length === 0)) && (
                      <div
                        onClick={() => recognizeLocationHandler()}
                        className="flex cursor-pointer items-center text-primary hover:text-primary/70"
                      >
                        <TbCurrentLocation className="h-6 w-6 group-data-[dir=ltr]:mr-4 group-data-[dir=rtl]:ml-4" />
                        {haveCustomPosition
                          ? t("current-location")
                          : t("locate-me")}
                      </div>
                    )}
                    {foundInTitle.map((poi) => (
                      <li
                        key={poi.id}
                        value={poi.id}
                        className="flex cursor-pointer lg:hover:text-primary"
                        onClick={() => onSelectHandler({ poi })}
                      >
                        <span className="flex-1">{poi.properties?.title}</span>
                        <span className="font-bold">{poi.floorName}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {foundInDescription.length > 0 && (
                  <ul className="flex flex-col gap-2">
                    <li className="mt-2 font-semibold text-muted-foreground/80">
                      {t("found-in-description")}
                    </li>
                    {foundInDescription.map((poi) => (
                      <li
                        key={poi.id}
                        value={poi.id}
                        className="flex cursor-pointer lg:hover:text-primary"
                        onClick={() => onSelectHandler({ poi })}
                      >
                        <span className="flex-1">{poi.properties?.title}</span>
                        <span className="font-bold">{poi.floorName}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {foundInDescription.length === 0 &&
                  foundInTitle.length === 0 && (
                    <p className="py-4 text-center text-sm">
                      {t("no-results")}
                    </p>
                  )}
              </div>
            </OverlayScrollbarsComponent>
          )}
        </div>
      </motion.div>
    </>
  );

  /*return (
    <>
      <motion.div
        key="list"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.2 }}
        className="absolute left-3 right-3 top-3 z-10 flex flex-col rounded-xl bg-white p-3 shadow-[0_3px_10px_rgb(0,0,0,0.05)]"
      >
        <div className="flex flex-col items-center gap-1 lg:block lg:flex-row">
          {startFeature !== "kiosk" && (
            <PoiSelect
              selectedPoi={startFeature}
              onSelect={startSelectHandler}
              placeholder={t("choose-start")}
            />
          )}
          {showCustomRoutePicker && (
            <PoiSelect
              selectedPoi={finishFeature}
              onSelect={finishSelectHandler}
              placeholder={t("destination")}
            />
          )}
          {activeFilter?.type === "closest" && <ClosestAmenitySelect />}
        </div>
      </motion.div>
    </>
  );*/
}

export default RouteForm;
